import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Fine-grained UI permissions (Cognito groups). Route-level rules live in portal-route-roles.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  constructor(private _auth: AuthService) {}

  private _roles(): string[] {
    return this._auth.getRoles() || [];
  }

  hasAnyRole(groups: string[]): boolean {
    const roles = this._roles();
    for (const g of groups) {
      if (roles.includes(g)) {
        return true;
      }
    }
    return false;
  }

  /** Read-only auditor role */
  isViewer(): boolean {
    return this._roles().includes('Viewer');
  }

  isCustomer(): boolean {
    return this._roles().includes('Customer');
  }

  /** Create/update/delete data (hide for Viewer) */
  canMutate(): boolean {
    return !this.isViewer();
  }

  /** Dashboard downloads / quick-create links */
  canMutateDashboard(): boolean {
    return !this.isViewer();
  }

  /**
   * Prescription review: staff can change status and use split/fulfil; Customer/Viewer read-only for workflow.
   */
  isPrescriptionReviewStaff(): boolean {
    return !this.isViewer() && !this.isCustomer();
  }

  /** Catalogue medicine edit (not PrescriptionManager — view-only for that role per product rules). */
  canEditMedicineCatalogue(): boolean {
    return this.hasAnyRole(['SuperAdmin', 'OperationsManager', 'Prescription']);
  }

  /** Individuals / companies: PrescriptionManager may view lists and detail, not add or edit records. */
  canAddOrEditIndividualOrCompany(): boolean {
    if (!this.canMutate()) {
      return false;
    }
    return !this._roles().includes('PrescriptionManager');
  }
}
